import React, { useEffect, useState, useRef } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAuthUser, useAuthStatus } from '@/contexts/AuthContext'
import { useServiceContext } from '@/contexts/ServiceContext'
import { browserScheduler } from '@/lib/scheduler'
import type { Scheduler } from '@/lib/scheduler'

interface LessonRouteGuardProps {
    children: React.ReactNode
}

const LessonRouteGuard: React.FC<LessonRouteGuardProps> = ({ children }) => {
    const { t } = useTranslation()
    const user = useAuthUser()
    const { hydrated } = useAuthStatus()
    const serviceContext = useServiceContext()
    // Use scheduler from context for testability, fallback to browserScheduler
    const currentScheduler = serviceContext?.scheduler ?? browserScheduler

    // Stabilize scheduler reference to prevent effect re-runs when provider recreates
    // Only update ref when scheduler actually changes (not just reference equality)
    const schedulerRef = useRef<Scheduler>(currentScheduler)
    if (schedulerRef.current !== currentScheduler) {
        schedulerRef.current = currentScheduler
    }
    const scheduler = schedulerRef.current

    const [isInitialized, setIsInitialized] = useState(false)

    // Wait for auth to be fully hydrated before rendering lesson content
    useEffect(() => {
        if (hydrated && user) {
            // Small delay to ensure all contexts are properly initialized
            const timer = scheduler.setTimeout(() => {
                setIsInitialized(true)
            }, 100)
            return () => scheduler.clearTimeout(timer)
        } else {
            setIsInitialized(false)
            return undefined
        }
    }, [hydrated, user, scheduler])

    // Show loading while auth is not hydrated yet
    if (!hydrated || !user || !isInitialized) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: (theme) => theme.spacing(25), // 200px = 25 * 8px
                    p: 3,
                }}
                role="status"
                aria-live="polite"
                aria-label={t('common:loading')}
            >
                <CircularProgress size={24} sx={{ mb: 2 }} />
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                    }}
                >
                    {t('common:loading')}
                </Typography>
            </Box>
        )
    }

    // Auth is hydrated and user exists, render children
    return <>{children}</>
}

export default LessonRouteGuard
